import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent, CookiePreferences } from '../../hooks/useCookieConsent';

interface CookieConsentProps {
  onAcceptAll?: () => void;
  onAcceptNecessary?: () => void;
  onCustomize?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({
  onAcceptAll,
  onAcceptNecessary,
  onCustomize
}) => {
  const { t } = useTranslation();
  const { 
    hasConsent, 
    preferences, 
    isLoading, 
    savePreferences, 
    acceptAll, 
    acceptNecessaryOnly 
  } = useCookieConsent();
  
  const [showCustomization, setShowCustomization] = useState(false);
  const [customPreferences, setCustomPreferences] = useState<CookiePreferences>(preferences);

  // No mostrar el banner si ya tiene consentimiento o está cargando
  if (isLoading || hasConsent) return null;

  const handleAcceptAll = async () => {
    await acceptAll();
    onAcceptAll?.();
  };

  const handleAcceptNecessary = async () => {
    await acceptNecessaryOnly();
    onAcceptNecessary?.();
  };

  const handleCustomSave = async () => {
    await savePreferences(customPreferences);
    setShowCustomization(false);
    onCustomize?.();
  };

  const handleCustomize = () => {
    setCustomPreferences(preferences);
    setShowCustomization(true);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    setCustomPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <>
      {/* Backdrop */}
      {showCustomization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9998]" />
      )}
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] transform transition-transform duration-300 translate-y-0">
        <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
          {!showCustomization ? (
            // Main Banner
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                         Uso de Cookies
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        Usamos cookies técnicas, necesarias para que la aplicación funcione, y, solo si las aceptas,
                        cookies analíticas de terceros (Google Analytics, Seline) para medir el uso. Puedes aceptarlas,
                        rechazarlas o elegir cuáles, y cambiar de opinión cuando quieras desde Ajustes.{' '}
                        <a 
                          href="/legal/cookies" 
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ver política completa
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
                  <button
                    onClick={handleCustomize}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors border border-gray-300 dark:border-gray-600"
                  >
                    Personalizar
                  </button>
                  <button
                    onClick={handleAcceptNecessary}
                    className="px-4 py-2 text-sm text-white bg-gray-900 hover:bg-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 rounded-md transition-colors font-medium"
                  >
                    Rechazar
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors font-medium"
                  >
                    Aceptar todas
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Customization Panel
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Configuración de Cookies
                  </h3>
                  <button
                    onClick={() => setShowCustomization(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Necessary Cookies */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Cookies Necesarias
                      </h4>
                      <div className="bg-gray-100 dark:bg-gray-700 rounded-full px-3 py-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Siempre activas</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Estas cookies son esenciales para el funcionamiento básico del sitio web y no se pueden desactivar. 
                      Incluyen funciones de autenticación, seguridad y navegación básica.
                    </p>
                  </div>

                  {/* Functional Cookies */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Cookies Funcionales
                      </h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customPreferences.functional}
                          onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Permiten recordar sus preferencias (idioma, región, configuración de pantalla) para mejorar 
                      su experiencia de navegación.
                    </p>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Cookies de Análisis
                      </h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customPreferences.analytics}
                          onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Nos ayudan a entender cómo los visitantes interactúan con el sitio web, recopilando información 
                      de forma anónima para mejorar nuestros servicios.
                    </p>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        Cookies de Marketing
                      </h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customPreferences.marketing}
                          onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Se utilizan para mostrar anuncios relevantes y medir la efectividad de nuestras campañas publicitarias.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => setShowCustomization(false)}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors border border-gray-300 dark:border-gray-600"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleCustomSave}
                    className="px-6 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors font-medium"
                  >
                    Guardar Configuración
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;